import { ChangeDetectionStrategy, Component, forwardRef, input, signal, ChangeDetectorRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

const WEEKDAYS = [
  { iso: 0, key: 'breadcrumb.dayShort.mon' },
  { iso: 1, key: 'breadcrumb.dayShort.tue' },
  { iso: 2, key: 'breadcrumb.dayShort.wed' },
  { iso: 3, key: 'breadcrumb.dayShort.thu' },
  { iso: 4, key: 'breadcrumb.dayShort.fri' },
  { iso: 5, key: 'breadcrumb.dayShort.sat' },
  { iso: 6, key: 'breadcrumb.dayShort.sun' },
] as const;

@Component({
  selector: 'zag-weekday-picker',
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './weekday-picker.scss',
  template: `
    @if (label()) {
      <span class="zag-weekday-picker__label">{{ label() }}</span>
    }
    <div class="zag-weekday-picker__days" role="group" [attr.aria-label]="label()">
      @for (day of weekdays; track day.iso) {
        <button
          type="button"
          class="zag-weekday-picker__day"
          [class.zag-weekday-picker__day--selected]="isSelected(day.iso)"
          [disabled]="disabled() || disabledDays().includes(day.iso)"
          [attr.aria-pressed]="isSelected(day.iso)"
          (click)="toggle(day.iso)"
        >
          {{ day.key | translate }}
        </button>
      }
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WeekdayPicker),
      multi: true,
    },
  ],
})
export class WeekdayPicker implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly disabled = input<boolean>(false);
  readonly disabledDays = input<readonly number[]>([]);
  readonly label = input<string>('');

  protected readonly weekdays = WEEKDAYS;
  protected readonly selectedDays = signal<number[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: number[]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(value: number[]): void {
    this.selectedDays.set(value ?? []);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected isSelected(day: number): boolean {
    return this.selectedDays().includes(day);
  }

  protected toggle(day: number): void {
    const current = this.selectedDays();
    const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day].sort((a, b) => a - b);
    this.selectedDays.set(next);
    this.onChange(next);
    this.onTouched();
  }
}
